// Acorn and seed types with rarities and growth requirements

// Growth stages - visual progression as seed grows
export const growthStages = [
  { minProgress: 0, maxProgress: 0.25, emoji: '🌰', name: 'Seed' },
  { minProgress: 0.25, maxProgress: 0.50, emoji: '🌱', name: 'Sprout' },
  { minProgress: 0.50, maxProgress: 0.75, emoji: '🌿', name: 'Seedling' },
  { minProgress: 0.75, maxProgress: 0.99, emoji: '🪴', name: 'Sapling' },
  { minProgress: 0.99, maxProgress: 1.0, emoji: '✨', name: 'Ready!' }
];

// Get current growth stage based on progress
export const getGrowthStage = (currentMiles, totalMiles) => {
  const progress = currentMiles / totalMiles;
  return growthStages.find(stage =>
    progress >= stage.minProgress && progress <= stage.maxProgress
  ) || growthStages[0];
};

// Mystery seed display names - users don't know what they'll become!
export const mysterySeeds = {
  common: { name: 'Common Seed', emoji: '🌰' },
  uncommon: { name: 'Uncommon Seed', emoji: '🌰' },
  rare: { name: 'Rare Seed', emoji: '🌰' },
  legendary: { name: 'Mystic Seed', emoji: '💎' }
};

export const acornTypes = [
  // Common (70% drop rate) - 3-5 miles
  {
    id: 'oak_acorn',
    rarity: 'common',
    milesNeeded: 3,
    result: { name: 'Young Oak', emoji: '🌳' }
  },
  {
    id: 'maple_seed',
    rarity: 'common',
    milesNeeded: 4,
    result: { name: 'Maple Tree', emoji: '🍁' }
  },
  {
    id: 'birch_seed',
    rarity: 'common',
    milesNeeded: 3,
    result: { name: 'Birch Tree', emoji: '🌿' }
  },
  {
    id: 'hickory_nut',
    rarity: 'common',
    milesNeeded: 4,
    result: { name: 'Hickory Tree', emoji: '🌳' }
  },
  {
    id: 'elm_seed',
    rarity: 'common',
    milesNeeded: 3,
    result: { name: 'Elm Tree', emoji: '🌲' }
  },
  {
    id: 'beech_nut',
    rarity: 'common',
    milesNeeded: 5,
    result: { name: 'Beech Tree', emoji: '🌳' }
  },
  {
    id: 'fir_cone',
    rarity: 'common',
    milesNeeded: 4,
    result: { name: 'Fir Tree', emoji: '🌲' }
  },
  {
    id: 'aspen_seed',
    rarity: 'common',
    milesNeeded: 3,
    result: { name: 'Aspen Tree', emoji: '🌳' }
  },
  {
    id: 'poplar_seed',
    rarity: 'common',
    milesNeeded: 4,
    result: { name: 'Poplar Tree', emoji: '🌲' }
  },
  {
    id: 'ash_seed',
    rarity: 'common',
    milesNeeded: 5,
    result: { name: 'Ash Tree', emoji: '🌳' }
  },
  {
    id: 'alder_seed',
    rarity: 'common',
    milesNeeded: 3,
    result: { name: 'Alder Tree', emoji: '🌿' }
  },
  {
    id: 'hawthorn_seed',
    rarity: 'common',
    milesNeeded: 4,
    result: { name: 'Hawthorn Tree', emoji: '🌸' }
  },
  {
    id: 'spruce_cone',
    rarity: 'common',
    milesNeeded: 5,
    result: { name: 'Spruce Tree', emoji: '🌲' }
  },

  // Uncommon (20% drop rate) - 6-8 miles
  {
    id: 'pine_cone',
    rarity: 'uncommon',
    milesNeeded: 6,
    result: { name: 'Pine Tree', emoji: '🌲' }
  },
  {
    id: 'cherry_pit',
    rarity: 'uncommon',
    milesNeeded: 7,
    result: { name: 'Cherry Blossom', emoji: '🌸' }
  },
  {
    id: 'willow_seed',
    rarity: 'uncommon',
    milesNeeded: 8,
    result: { name: 'Willow Tree', emoji: '🌳' }
  },
  {
    id: 'walnut_seed',
    rarity: 'uncommon',
    milesNeeded: 7,
    result: { name: 'Walnut Tree', emoji: '🌰' }
  },
  {
    id: 'apple_seed',
    rarity: 'uncommon',
    milesNeeded: 6,
    result: { name: 'Apple Tree', emoji: '🍎' }
  },
  {
    id: 'mulberry_seed',
    rarity: 'uncommon',
    milesNeeded: 8,
    result: { name: 'Mulberry Tree', emoji: '🫐' }
  },
  {
    id: 'cedar_seed',
    rarity: 'uncommon',
    milesNeeded: 7,
    result: { name: 'Cedar Tree', emoji: '🌲' }
  },
  {
    id: 'cypress_seed',
    rarity: 'uncommon',
    milesNeeded: 8,
    result: { name: 'Cypress Tree', emoji: '🌳' }
  },
  {
    id: 'dogwood_seed',
    rarity: 'uncommon',
    milesNeeded: 6,
    result: { name: 'Dogwood Tree', emoji: '🌸' }
  },
  {
    id: 'juniper_seed',
    rarity: 'uncommon',
    milesNeeded: 7,
    result: { name: 'Juniper Tree', emoji: '🌿' }
  },
  {
    id: 'pear_seed',
    rarity: 'uncommon',
    milesNeeded: 6,
    result: { name: 'Pear Tree', emoji: '🍐' }
  },
  {
    id: 'plum_seed',
    rarity: 'uncommon',
    milesNeeded: 8,
    result: { name: 'Plum Tree', emoji: '🟣' }
  },
  {
    id: 'hazelnut_seed',
    rarity: 'uncommon',
    milesNeeded: 7,
    result: { name: 'Hazelnut Tree', emoji: '🌰' }
  },

  // Rare (8% drop rate) - 10-15 miles
  {
    id: 'golden_acorn',
    rarity: 'rare',
    milesNeeded: 10,
    result: { name: 'Golden Oak', emoji: '✨' }
  },
  {
    id: 'redwood_cone',
    rarity: 'rare',
    milesNeeded: 12,
    result: { name: 'Giant Redwood', emoji: '🌲' }
  },
  {
    id: 'magnolia_seed',
    rarity: 'rare',
    milesNeeded: 15,
    result: { name: 'Magnolia Tree', emoji: '🌺' }
  },
  {
    id: 'pecan_nut',
    rarity: 'rare',
    milesNeeded: 12,
    result: { name: 'Pecan Tree', emoji: '🥜' }
  },
  {
    id: 'wild_cherry_pit',
    rarity: 'rare',
    milesNeeded: 10,
    result: { name: 'Wild Cherry', emoji: '🍒' }
  },
  {
    id: 'chestnut_seed',
    rarity: 'rare',
    milesNeeded: 12,
    result: { name: 'Chestnut Tree', emoji: '🌰' }
  },
  {
    id: 'sequoia_cone',
    rarity: 'rare',
    milesNeeded: 15,
    result: { name: 'Sequoia Tree', emoji: '🌲' }
  },
  {
    id: 'persimmon_seed',
    rarity: 'rare',
    milesNeeded: 10,
    result: { name: 'Persimmon Tree', emoji: '🟠' }
  },
  {
    id: 'fig_seed',
    rarity: 'rare',
    milesNeeded: 13,
    result: { name: 'Fig Tree', emoji: '🟤' }
  },
  {
    id: 'bamboo_shoot',
    rarity: 'rare',
    milesNeeded: 11,
    result: { name: 'Bamboo Grove', emoji: '🎋' }
  },

  // Legendary (2% drop rate) - 20+ miles
  {
    id: 'crystal_seed',
    rarity: 'legendary',
    milesNeeded: 20,
    result: { name: 'Crystal Tree', emoji: '💠' }
  },
  {
    id: 'rainbow_acorn',
    rarity: 'legendary',
    milesNeeded: 25,
    result: { name: 'Rainbow Tree', emoji: '🌈' }
  },
  {
    id: 'ancient_seed',
    rarity: 'legendary',
    milesNeeded: 30,
    result: { name: 'Ancient Grove', emoji: '🏛️' }
  },
  {
    id: 'baobab_seed',
    rarity: 'legendary',
    milesNeeded: 25,
    result: { name: 'Baobab Tree', emoji: '🌳' }
  },
  {
    id: 'joshua_seed',
    rarity: 'legendary',
    milesNeeded: 22,
    result: { name: 'Joshua Tree', emoji: '🌵' }
  },
];

export const rarityWeights = {
  common: 70,
  uncommon: 20,
  rare: 8,
  legendary: 2
};

export const rarityColors = {
  common: {
    light: 'bg-slate-100 text-slate-700 border-slate-300',
    dark: 'bg-slate-800 text-slate-300 border-slate-600'
  },
  uncommon: {
    light: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    dark: 'bg-emerald-900 text-emerald-300 border-emerald-600'
  },
  rare: {
    light: 'bg-blue-100 text-blue-700 border-blue-300',
    dark: 'bg-blue-900 text-blue-300 border-blue-600'
  },
  legendary: {
    light: 'bg-purple-100 text-purple-700 border-purple-300',
    dark: 'bg-purple-900 text-purple-300 border-purple-600'
  }
};

// Generate a random acorn based on rarity weights
export const generateRandomAcorn = () => {
  const total = Object.values(rarityWeights).reduce((a, b) => a + b, 0);
  const random = Math.random() * total;

  let cumulative = 0;
  let selectedRarity = 'common';

  for (const [rarity, weight] of Object.entries(rarityWeights)) {
    cumulative += weight;
    if (random <= cumulative) {
      selectedRarity = rarity;
      break;
    }
  }

  // Get all acorns of this rarity
  const acornsOfRarity = acornTypes.filter(a => a.rarity === selectedRarity);

  // Pick a random one
  const selectedAcorn = acornsOfRarity[Math.floor(Math.random() * acornsOfRarity.length)];

  // Get mystery seed display info based on rarity
  const mysterySeed = mysterySeeds[selectedRarity];

  return {
    ...selectedAcorn,
    name: mysterySeed.name,  // Display as mystery seed
    emoji: mysterySeed.emoji, // Display mystery emoji
    id: `${selectedAcorn.id}_${Date.now()}_${Math.random()}` // Unique instance ID
  };
};

// Calculate how many acorns to drop based on distance
export const calculateAcornDrops = (distance) => {
  if (!distance) return 0;

  // Base: 1 acorn per 5 miles (more rare)
  const baseDrops = Math.floor(distance / 5);

  // Chance for bonus acorn based on remainder
  const bonusChance = (distance % 5) / 5;
  const bonus = Math.random() < bonusChance ? 1 : 0;

  return baseDrops + bonus; // No guaranteed minimum - seeds are rare!
};
